import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "../components/site/AdminNav";
import { Input } from "../components/ui/input";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatARS } from "../lib/site";
import { TransferAdmin } from "../components/site/TransferAdmin";
import { FarePromosAdmin, PromoResults } from "../components/site/FarePromosAdmin";
import { DestStats } from "../components/site/DestStats";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" });
const monthStart = () => `${today().slice(0, 8)}01`;
const TABS = [["paradas", "Pasajeros por parada"], ["comisiones", "Comisiones"], ["codigos", "Códigos de descuento"], ["transferencias", "Transferencias"], ["promos", "Promos de tramo"], ["resultados-promos", "Resultados de promos"], ["destinos", "Destinos más buscados"]];

const useLoad = (url, params, deps) => {
  const [data, setData] = useState(null);
  const load = () => { setData(null); api.get(url, { params }).then((r) => setData(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setData([]); }); };
  useEffect(() => { load(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
};

const Pax = ({ p }) => (
  <tr className="border-t border-black/5">
    <td className="py-1.5 pr-3 font-mono">{p.seat}</td><td className="pr-3">{p.name} <span className="text-[var(--muted-ink)]">· {p.dni}</span></td>
    <td className="pr-3">{p.to}</td><td className="pr-3">{formatARS(p.price ?? 0)}{p.promo ? ` (${p.promo})` : ""}</td>
    <td className="pr-3">{p.status === "confirmado" ? `Pagado · ${p.payment ?? ""} · ${p.office ?? ""}` : "Reservado sin pagar"}</td><td className="font-mono text-xs">{p.code}</td>
  </tr>
);

const Boardings = () => {
  const [date, setDate] = useState(today());
  const [trips] = useLoad("/admin/reports/boardings", { date }, [date]);
  return (
    <div data-testid="report-boardings" className="space-y-4">
      <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="report-date" className="h-11 rounded-xl border border-black/10 bg-white px-3" />
      {!trips ? <Loader2 className="h-5 w-5 animate-spin" /> : !trips.length ? <p className="text-[var(--muted-ink)]">No hay servicios ese día.</p> : trips.map((t) => (
        <div key={t.trip_id} data-testid={`report-trip-${t.trip_id}`} className="overflow-x-auto rounded-3xl bg-white p-5">
          <p className="font-display text-lg">{t.route} · {t.departure} hs <span className="text-sm text-[var(--muted-ink)]">· {t.passengers} pasajeros</span></p>
          {!t.stops.length && <p className="mt-2 text-sm text-[var(--muted-ink)]">Sin pasajeros todavía.</p>}
          {t.stops.map((s) => (
            <div key={s.idx} data-testid={`report-stop-${t.trip_id}-${s.idx}`} className="mt-4">
              <p className="text-sm font-bold">Sube en {s.stop} · {s.passengers.length} · cobrado {formatARS(s.total)}</p>
              <table className="mt-1 w-full min-w-[640px] text-left text-sm"><tbody>{s.passengers.map((p) => <Pax key={`${p.code}-${p.seat}`} p={p} />)}</tbody></table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const Commissions = () => {
  const [range, setRange] = useState({ date_from: monthStart(), date_to: today() });
  const [rows] = useLoad("/admin/reports/commissions", range, [range.date_from, range.date_to]);
  return (
    <div data-testid="report-commissions" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {["date_from", "date_to"].map((k) => <input key={k} type="date" value={range[k]} onChange={(e) => e.target.value && setRange({ ...range, [k]: e.target.value })} data-testid={`commission-${k}`} className="h-11 rounded-xl border border-black/10 bg-white px-3" />)}
      </div>
      <p className="text-xs text-[var(--muted-ink)]">Pasajes pagados en el período, vendidos en el mostrador de la agencia o en la web con su código. El % se carga en Agencias.</p>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin" /> : !rows.length ? <p className="text-[var(--muted-ink)]">Sin ventas con comisión en el período.</p> : (
        <div className="overflow-x-auto rounded-3xl bg-white p-5"><table className="w-full min-w-[560px] text-left text-sm">
          <thead><tr className="text-[var(--muted-ink)]"><th>Agencia</th><th>Pasajes</th><th>Mostrador / web</th><th>Vendido</th><th>%</th><th>Comisión</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.agency_id} data-testid={`commission-row-${r.agency_id}`} className="border-t border-black/5"><td className="py-2 font-semibold">{r.name}</td><td>{r.tickets}</td><td>{r.counter} / {r.web}</td><td>{formatARS(r.sold)}</td><td>{r.pct}%</td><td className="font-bold text-[var(--forest)]">{formatARS(r.commission)}</td></tr>)}</tbody>
        </table></div>
      )}
    </div>
  );
};

const EMPTY = { code: "", pct: 10, expires: "", max_uses: "", destinations: "" };

const Codes = () => {
  const [list, load] = useLoad("/admin/promos", {}, []);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", { code: f.code, pct: Number(f.pct), expires: f.expires || null, max_uses: f.max_uses ? Number(f.max_uses) : null,
        destinations: f.destinations.split(",").map((x) => x.trim()).filter(Boolean), active: true });
      toast.success("Código creado"); setF(EMPTY); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const toggle = async (p) => { await api.put(`/admin/promos/${p.id}`, { ...p, active: !p.active }).catch((e) => toast.error(apiError(e))); load(); };
  const remove = async (p) => { if (window.confirm(`¿Borrar ${p.code}?`)) { await api.delete(`/admin/promos/${p.id}`); load(); } };
  return (
    <div data-testid="report-codes" className="space-y-4">
      <form onSubmit={save} className="grid gap-2 rounded-3xl bg-white p-5 sm:grid-cols-6">
        <Input data-testid="promo-form-code" required placeholder="CÓDIGO" value={f.code} onChange={set("code")} className="h-11 rounded-xl uppercase sm:col-span-1" />
        <Input data-testid="promo-form-pct" required type="number" min="1" max="100" placeholder="%" value={f.pct} onChange={set("pct")} className="h-11 rounded-xl" />
        <Input data-testid="promo-form-expires" type="date" value={f.expires} onChange={set("expires")} className="h-11 rounded-xl" title="Vence (opcional)" />
        <Input data-testid="promo-form-max" type="number" min="1" placeholder="Usos máx." value={f.max_uses} onChange={set("max_uses")} className="h-11 rounded-xl" />
        <Input data-testid="promo-form-destinations" placeholder="Destinos (ej. Tucumán, Frías) o vacío = todos" value={f.destinations} onChange={set("destinations")} className="h-11 rounded-xl sm:col-span-2" />
        <button data-testid="promo-form-submit" className="h-11 rounded-xl bg-[var(--forest)] font-bold text-white sm:col-span-6">Crear código</button>
      </form>
      {!list ? <Loader2 className="h-5 w-5 animate-spin" /> : list.map((p) => (
        <div key={p.id} data-testid={`promo-row-${p.code}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white p-4 text-sm">
          <p><b className="font-mono text-base">{p.code}</b> · {p.pct}% · {p.destinations?.length ? p.destinations.join(", ") : "todos los destinos"} · {p.expires ? `vence ${p.expires}` : "sin vencimiento"} · usos {p.uses}{p.max_uses ? `/${p.max_uses}` : ""}</p>
          <div className="flex gap-2">
            <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.code}`} className={`rounded-full px-4 py-1.5 font-bold ${p.active ? "bg-[var(--lime)]" : "bg-black/5"}`}>{p.active ? "Activo" : "Pausado"}</button>
            <button onClick={() => remove(p)} data-testid={`promo-delete-${p.code}`} aria-label="Borrar" className="rounded-full p-2 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-700" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PANELS = { paradas: Boardings, comisiones: Commissions, codigos: Codes, transferencias: TransferAdmin, promos: FarePromosAdmin, "resultados-promos": PromoResults, destinos: DestStats };

export default function AdminReportes() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => { const q = new URLSearchParams(window.location.search).get("tab"); return TABS.some(([k]) => k === q) ? q : "paradas"; });
  const [prefill, setPrefill] = useState(null);
  const go = (t, pf) => { setPrefill(pf || null); setTab(t); };
  if (user === null) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/admin/trafico" replace />;
  return (
    <section data-testid="admin-reports-page" className="min-h-[75vh] bg-[var(--paper)] pb-24">
      <div className="mx-auto max-w-[1100px] px-5 pt-12 md:px-10">
        <AdminNav title="Ventas" />
        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map(([k, l]) => <button key={k} onClick={() => go(k)} data-testid={`reports-tab-${k}`} className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === k ? "bg-[var(--ink)] text-white" : "bg-white"}`}>{l}</button>)}
        </div>
        <div className="mt-6">{(() => { const P = PANELS[tab]; return <P go={go} prefill={prefill} />; })()}</div>
      </div>
    </section>
  );
}
